import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import ReactMarkdown, { type Components } from "react-markdown";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/**
 * What a bio or a description may contain. Anything else is unwrapped to its text, so a pasted
 * image or table reads as plain words instead of breaking the row it sits in. Raw HTML never
 * renders: `skipHtml` drops it before it reaches React.
 */
const ALLOWED_ELEMENTS = [
  "p",
  "br",
  "strong",
  "em",
  "a",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "code",
];

/** Descriptions sit inside cards and dialogs that own the real headings, so these stay small. */
function Heading({ children }: { children?: ReactNode }) {
  return <h4 className="font-medium text-foreground">{children}</h4>;
}

const COMPONENTS: Components = {
  h1: ({ children }) => <Heading>{children}</Heading>,
  h2: ({ children }) => <Heading>{children}</Heading>,
  h3: ({ children }) => <Heading>{children}</Heading>,
  h4: ({ children }) => <Heading>{children}</Heading>,
  h5: ({ children }) => <Heading>{children}</Heading>,
  h6: ({ children }) => <Heading>{children}</Heading>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-foreground underline underline-offset-4 hover:text-primary"
    >
      {children}
    </a>
  ),
  ul: ({ children }) => <ul className="list-disc space-y-1 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal space-y-1 pl-5">{children}</ol>,
  strong: ({ children }) => <strong className="font-medium text-foreground">{children}</strong>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-border pl-3">{children}</blockquote>,
  code: ({ children }) => <code className="rounded bg-muted px-1 font-mono text-[13px]">{children}</code>,
};

export interface BookingMarkdownProps {
  /** Markdown as the team typed it. */
  source: string;
  className?: string;
  /**
   * Collapse the text to about this many lines and offer "Show more" when it does not fit. The
   * height comes from the text's own line height, so it follows whatever size the caller sets.
   */
  clampLines?: number;
}

/** How long the open and close takes. The class below carries the same number. */
const TOGGLE_MS = 200;

/** How much of the collapsed text's bottom fades out. */
const FADE = "3rem";

/**
 * Fades the bottom of the collapsed text through a mask, not a painted gradient, so it blends
 * into whatever surface is behind it: a card, the page, a dialog, light or dark.
 */
const FADE_MASK = `linear-gradient(to bottom, black calc(100% - ${FADE}), transparent)`;

/** The height of one line of the text, read from the page. jsdom and odd fonts get a sane guess. */
const lineHeightOf = (element: HTMLElement): number => {
  const style = window.getComputedStyle(element);
  const lineHeight = Number.parseFloat(style.lineHeight);
  if (Number.isFinite(lineHeight) && lineHeight > 0) return lineHeight;
  const fontSize = Number.parseFloat(style.fontSize);
  return Number.isFinite(fontSize) && fontSize > 0 ? fontSize * 1.5 : 20;
};

/**
 * A bio or a description, written in Markdown and shown as quiet prose. Links open in a new tab.
 * Images and raw HTML are left out.
 *
 * With `clampLines`, long text collapses to that height with its last lines fading out, and a
 * button under it opens the rest. The height eases between the two. Text that fits shows whole,
 * with no fade and no button.
 */
export function BookingMarkdown({ source, className, clampLines }: BookingMarkdownProps) {
  const regionId = useId();
  const content = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  // While the height eases, the box keeps clipping; once open and settled it lets go entirely.
  const [moving, setMoving] = useState(false);
  const [heights, setHeights] = useState<{ collapsed: number; full: number } | null>(null);

  useLayoutEffect(() => {
    const element = content.current;
    if (!element || clampLines === undefined) return;

    const measure = () => {
      const next = { collapsed: Math.round(lineHeightOf(element) * clampLines), full: element.scrollHeight };
      setHeights((last) => (last && last.collapsed === next.collapsed && last.full === next.full ? last : next));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [clampLines, source]);

  // The fallback for a transition that never reports its end (reduced motion, a hidden tab).
  useEffect(() => {
    if (!moving) return;
    const timer = window.setTimeout(() => setMoving(false), TOGGLE_MS + 50);
    return () => window.clearTimeout(timer);
  }, [moving]);

  const overflows = heights !== null && heights.full > heights.collapsed + 1;
  const collapsed = overflows && !expanded;

  const clip: CSSProperties | undefined = !overflows
    ? undefined
    : collapsed
      ? {
          maxHeight: heights.collapsed,
          overflow: "hidden",
          maskImage: moving ? undefined : FADE_MASK,
          WebkitMaskImage: moving ? undefined : FADE_MASK,
        }
      : moving
        ? { maxHeight: heights.full, overflow: "hidden" }
        : undefined;

  return (
    <div className={cn("min-w-0 text-sm text-muted-foreground", className)}>
      <div
        id={regionId}
        style={clip}
        data-clamped={collapsed ? "" : undefined}
        // Says the bottom is fading out; jsdom drops `mask-image`, so tests read this instead.
        data-fade={collapsed && !moving ? "" : undefined}
        onTransitionEnd={(event) => {
          if (event.target === event.currentTarget) setMoving(false);
        }}
        className={cn(overflows && "transition-[max-height] duration-200 ease-out motion-reduce:transition-none")}
      >
        <div ref={content} className="space-y-2 break-words">
          <ReactMarkdown skipHtml unwrapDisallowed allowedElements={ALLOWED_ELEMENTS} components={COMPONENTS}>
            {source}
          </ReactMarkdown>
        </div>
      </div>
      {overflows ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={expanded}
          aria-controls={regionId}
          // Pulled left by its own padding, so the label starts where the text does.
          className="relative z-10 mt-1.5 -ml-2 h-7 gap-1 px-2 text-xs font-medium text-foreground has-[>svg]:px-2"
          onClick={() => {
            setMoving(true);
            setExpanded((open) => !open);
          }}
        >
          {expanded ? m.booking_markdown_show_less() : m.booking_markdown_show_more()}
          {expanded ? <ChevronUp aria-hidden className="size-3.5" /> : <ChevronDown aria-hidden className="size-3.5" />}
        </Button>
      ) : null}
    </div>
  );
}
